import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  type ClientStatus,
  type KycStatus,
  type SanctionsStatus,
  type CountryCode,
  type EntityType,
  type Sector,
  type UserStatus,
  type ClientContactRole,
  type RequestStatus,
  SERVICES,
} from '@shared';
import { apiGet, apiPatch, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { ClientEditDrawer } from './ClientEditDrawer';
import { InviteContactModal } from './InviteContactModal';
import { StatusChip } from '../../components/StatusChip';

// Roles that can edit the client identity / status. Server is the authority
// (REQ-004); this is defence-in-depth + UX affordance.
const CLIENT_EDIT_ROLES = new Set(['super', 'admin', 'lead', 'compliance']);
// Roles that can invite a new authorised contact (kept narrower per council
// notes — compliance reviews KYC + eligibility but doesn't grant portal access).
const CLIENT_INVITE_ROLES = new Set(['super', 'admin', 'lead']);

interface ClientDto {
  id: string;
  name: string;
  trading_name?: string | null;
  entity_type: EntityType;
  registration_number: string;
  country: CountryCode;
  sector: Sector;
  vertical?: string | null;
  registered_address?: string | null;
  website?: string | null;
  status: ClientStatus;
  kyc: KycStatus;
  kyc_depth: 'simplified' | 'standard' | 'enhanced';
  sanctions_status: SanctionsStatus;
  lead_staff_id?: string | null;
  joined_at: string;
  eligibility: {
    body_corporate_confirmed: boolean;
    sophisticated_self_cert: boolean;
    not_advice_acknowledged: boolean;
    terms_accepted: boolean;
    accepted_at?: string | null;
    accepted_by_user_id?: string | null;
  };
}

interface ContactDto {
  id: string;
  name: string;
  email: string;
  contact_role: ClientContactRole;
  phone: string | null;
  initials: string;
  status: UserStatus;
  last_active_at?: string | null;
  created_at: string;
}

interface LeadOption {
  id: string;
  name: string;
  role: string;
  initials: string;
}

// Audit-timeline row (subset of /api/audit response shape).
interface AuditRow {
  id: string;
  kind: string;
  actor_name: string;
  actor_kind: 'staff' | 'client' | 'system';
  what: string;
  at: string;
  d: string;
  t: string;
}

// Per-client drill-down row shapes — match GET /api/clients/:id/<resource>
// projections in src/server/routes/clients.ts.
interface RequestRow {
  id: string;
  headline: string;
  service: string;
  status: RequestStatus;
  urgency: 'standard' | 'expedited' | 'exploratory';
  value_pence: number | null;
  assigned_lead_staff_id: string | null;
  created_at: string;
}
interface ProposalRow {
  id: string;
  request_id: string;
  status: 'draft' | 'sent' | 'accepted' | 'declined' | 'withdrawn' | 'expired';
  fee_pence: number;
  currency: string;
  payment_plan: string;
  valid_until: string | null;
  sent_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  created_at: string;
}
interface InvoiceRow {
  id: string;
  proposal_id: string;
  request_id: string;
  status: 'issued' | 'awaiting_wire' | 'received' | 'cleared' | 'void' | 'refunded';
  amount_pence: number;
  currency: string;
  milestone_label: string | null;
  payment_reference: string;
  issued_at: string | null;
  due_at: string | null;
  created_at: string;
}
interface DocumentRow {
  id: string;
  kind: string;
  title: string;
  filename: string;
  status: string;
  visible_to_client: boolean;
  request_id: string | null;
  mime_type: string;
  size_bytes: number;
  created_at: string;
}

const STATUS_LABEL: Record<ClientStatus, string> = {
  prospect: 'Prospect',
  active: 'Active',
  suspended: 'Suspended',
  deactivated: 'Deactivated',
};

const USER_STATUS_LABEL: Record<UserStatus, string> = {
  invited: 'Invited',
  active: 'Active',
  suspended: 'Suspended',
  deactivated: 'Deactivated',
};

function statusTone(s: ClientStatus): string {
  return s === 'active' ? 'green'
    : s === 'suspended' ? 'amber'
    : s === 'deactivated' ? 'red'
    : 'accent'; // prospect
}

function kycTone(k: KycStatus): string {
  return k === 'current' ? 'green'
    : k === 'refresh' || k === 'pending' ? 'amber'
    : 'red';
}

function sanctionsTone(s: SanctionsStatus): string {
  return s === 'clear' ? 'green' : s === 'flagged' ? 'red' : 'amber';
}

function userStatusTone(s: UserStatus): string {
  return s === 'active' ? 'green'
    : s === 'invited' ? 'amber'
    : s === 'suspended' ? 'amber'
    : 'red';
}

function proposalStatusTone(s: ProposalRow['status']): string {
  return s === 'accepted' ? 'green'
    : s === 'sent' ? 'accent'
    : s === 'declined' || s === 'withdrawn' || s === 'expired' ? 'red'
    : '';
}
function invoiceStatusTone(s: InvoiceRow['status']): string {
  return s === 'cleared' ? 'green'
    : s === 'issued' || s === 'awaiting_wire' || s === 'received' ? 'amber'
    : s === 'void' ? 'red'
    : s === 'refunded' ? 'red'
    : '';
}
function fmtMoney(pence: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(pence / 100);
  } catch {
    return `${currency} ${(pence / 100).toLocaleString()}`;
  }
}

export function AdminClientDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const role = user?.role ?? '';
  const canEdit = CLIENT_EDIT_ROLES.has(role);
  const canInvite = CLIENT_INVITE_ROLES.has(role);

  const [client, setClient] = useState<ClientDto | null>(null);
  const [contacts, setContacts] = useState<ContactDto[]>([]);
  const [leads, setLeads] = useState<LeadOption[]>([]);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [proposals, setProposals] = useState<ProposalRow[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [activatingId, setActivatingId] = useState<string | null>(null);

  const refreshClient = useCallback(() => {
    if (!id) return;
    apiGet<{ client: ClientDto }>(`/api/clients/${id}`)
      .then((r) => setClient(r.client))
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.serverMessage : 'Failed to load client.'),
      );
  }, [id]);

  const refreshContacts = useCallback(() => {
    if (!id) return;
    apiGet<{ contacts: ContactDto[] }>(`/api/clients/${id}/contacts`)
      .then((r) => setContacts(r.contacts))
      .catch(() => setContacts([]));
  }, [id]);

  const refreshAudit = useCallback(() => {
    if (!id) return;
    // Only staff with audit:read can hit this; soft-fail to empty silently
    // for roles that can't (e.g. lead doesn't have audit:read).
    const params = new URLSearchParams({
      resource: 'clients',
      resource_id: id,
      limit: '50',
    });
    apiGet<{ entries: AuditRow[] }>(`/api/audit?${params.toString()}`)
      .then((r) => setAudit(r.entries))
      .catch(() => setAudit([]));
  }, [id]);

  // Per-client drill-down — four parallel fetches, each soft-fails to empty
  // (a role that can't read one resource shouldn't block the others).
  const refreshDrill = useCallback(() => {
    if (!id) return;
    apiGet<{ requests: RequestRow[] }>(`/api/clients/${id}/requests`)
      .then((r) => setRequests(r.requests)).catch(() => setRequests([]));
    apiGet<{ proposals: ProposalRow[] }>(`/api/clients/${id}/proposals`)
      .then((r) => setProposals(r.proposals)).catch(() => setProposals([]));
    apiGet<{ invoices: InvoiceRow[] }>(`/api/clients/${id}/invoices`)
      .then((r) => setInvoices(r.invoices)).catch(() => setInvoices([]));
    apiGet<{ documents: DocumentRow[] }>(`/api/clients/${id}/documents`)
      .then((r) => setDocuments(r.documents)).catch(() => setDocuments([]));
  }, [id]);

  // Lead name lookup — small list, fetch once.
  useEffect(() => {
    apiGet<{ leads: LeadOption[] }>('/api/users/leads')
      .then((r) => setLeads(r.leads))
      .catch(() => setLeads([]));
  }, []);

  useEffect(() => {
    refreshClient();
    refreshContacts();
    refreshAudit();
    refreshDrill();
  }, [refreshClient, refreshContacts, refreshAudit, refreshDrill]);

  // Clear the flash banner after 4s so it doesn't linger on the page.
  useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => setFlash(null), 4000);
    return () => window.clearTimeout(t);
  }, [flash]);

  // Activate an invited contact (flips status invited → active and fires the
  // sign-in invite email server-side). Optimistically refreshes contacts +
  // audit on success; the contact status chip flips to 'Active'.
  async function activateContact(userId: string, name: string): Promise<void> {
    if (!id) return;
    setActivatingId(userId);
    try {
      await apiPatch(`/api/clients/${id}/contacts/${userId}`, { status: 'active' });
      setFlash(`Activated ${name}. Sign-in invite sent.`);
      refreshContacts();
      refreshAudit();
    } catch (e) {
      setFlash(e instanceof ApiError ? `Activation failed: ${e.serverMessage}` : 'Activation failed.');
    } finally {
      setActivatingId(null);
    }
  }

  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--red)', color: 'var(--red)' }}>
        {error}
      </div>
    );
  }
  if (!client) {
    return <div style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</div>;
  }

  const leadResolved = client.lead_staff_id
    ? leads.find((l) => l.id === client.lead_staff_id)
    : null;

  return (
    <>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="client-detail-hdr">
        <div>
          <div className="client-detail-crumb">
            <Link to="/admin/clients">← All clients</Link>
          </div>
          <h1 className="portal-h1">{client.name}</h1>
          {client.trading_name && (
            <div className="client-detail-trading">trading as <strong>{client.trading_name}</strong></div>
          )}
          <div className="client-detail-chips">
            <span className={`chip ${statusTone(client.status)}`}>{STATUS_LABEL[client.status]}</span>
            <span className={`chip ${kycTone(client.kyc)}`}>KYC {client.kyc}</span>
            <span className={`chip ${sanctionsTone(client.sanctions_status)}`}>
              Sanctions {client.sanctions_status}
            </span>
          </div>
          <p className="portal-sub client-detail-meta">
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{client.id}</span>
            <span> · </span>
            <span>{client.entity_type}</span>
            <span> · </span>
            <span>{client.country}</span>
            <span> · </span>
            <span>{client.sector}</span>
          </p>
        </div>
        <div className="client-detail-actions">
          {canInvite && (
            <button type="button" className="btn" onClick={() => setInviteOpen(true)}>
              + Invite contact
            </button>
          )}
          {canEdit && (
            <button type="button" className="btn accent" onClick={() => setEditOpen(true)}>
              Edit
            </button>
          )}
        </div>
      </div>

      {flash && (
        <div className="client-detail-flash" role="status">{flash}</div>
      )}

      {/* ── Identity ───────────────────────────────────────────────────── */}
      <section className="card client-detail-section">
        <h3 className="client-detail-h">Identity</h3>
        <dl className="client-detail-kv">
          <dt>Legal name</dt>
          <dd>{client.name}</dd>

          <dt>Trading name</dt>
          <dd>{client.trading_name || <span className="muted-dash">—</span>}</dd>

          <dt>Entity type</dt>
          <dd>{client.entity_type}</dd>

          <dt>Registration number</dt>
          <dd style={{ fontFamily: 'var(--mono)' }}>{client.registration_number}</dd>

          <dt>Country</dt>
          <dd>{client.country}</dd>

          <dt>Sector</dt>
          <dd>{client.sector}{client.vertical ? ` · ${client.vertical}` : ''}</dd>

          <dt>Registered address</dt>
          <dd style={{ whiteSpace: 'pre-wrap' }}>
            {client.registered_address || <span className="muted-dash">—</span>}
          </dd>

          <dt>Website</dt>
          <dd>
            {client.website ? (
              <a href={client.website} target="_blank" rel="noopener noreferrer">{client.website}</a>
            ) : (
              <span className="muted-dash">—</span>
            )}
          </dd>
        </dl>
      </section>

      {/* ── Eligibility & KYC ─────────────────────────────────────────── */}
      <section className="card client-detail-section">
        <h3 className="client-detail-h">Eligibility & KYC</h3>
        <p className="client-create-help" style={{ margin: '0 0 8px' }}>
          FCA-perimeter gates (REQ-013). The client re-affirms via the portal
          at first sign-in; updates flow through PATCH /clients/:id/eligibility.
        </p>
        <ul className="client-detail-checks">
          <li className={client.eligibility.body_corporate_confirmed ? 'ok' : 'no'}>
            {client.eligibility.body_corporate_confirmed ? '✓' : '✗'} Body corporate confirmed
          </li>
          <li className={client.eligibility.sophisticated_self_cert ? 'ok' : 'no'}>
            {client.eligibility.sophisticated_self_cert ? '✓' : '✗'} Sophisticated / HNW self-cert
          </li>
          <li className={client.eligibility.not_advice_acknowledged ? 'ok' : 'no'}>
            {client.eligibility.not_advice_acknowledged ? '✓' : '✗'} Not-advice acknowledged
          </li>
          <li className={client.eligibility.terms_accepted ? 'ok' : 'no'}>
            {client.eligibility.terms_accepted ? '✓' : '✗'} Terms of Business accepted
          </li>
        </ul>

        <dl className="client-detail-kv" style={{ marginTop: 12 }}>
          <dt>KYC status</dt>
          <dd>
            <span className={`chip ${kycTone(client.kyc)}`}>{client.kyc}</span>
            <span style={{ color: 'var(--fg-mute)', fontSize: 12, marginLeft: 8 }}>
              ({client.kyc_depth})
            </span>
          </dd>

          <dt>Sanctions</dt>
          <dd>
            <span className={`chip ${sanctionsTone(client.sanctions_status)}`}>
              {client.sanctions_status}
            </span>
          </dd>

          <dt>Accepted</dt>
          <dd>
            {client.eligibility.accepted_at
              ? new Date(client.eligibility.accepted_at).toISOString().slice(0, 10)
              : <span className="muted-dash">pending</span>}
            {client.eligibility.accepted_by_user_id && (
              <span style={{ color: 'var(--fg-mute)', fontSize: 12, marginLeft: 8 }}>
                by {client.eligibility.accepted_by_user_id}
              </span>
            )}
          </dd>
        </dl>
        <p className="client-create-help" style={{ marginTop: 8 }}>
          KYC depth + status update on the <Link to="/admin/compliance/register">Compliance · KYC register</Link> page.
        </p>
      </section>

      {/* ── Engagement lead ───────────────────────────────────────────── */}
      <section className="card client-detail-section">
        <h3 className="client-detail-h">Engagement lead</h3>
        {leadResolved ? (
          <div className="client-detail-lead">
            <div className="client-detail-lead-initials" aria-hidden="true">
              {leadResolved.initials}
            </div>
            <div>
              <div style={{ fontWeight: 500 }}>{leadResolved.name}</div>
              <div style={{ fontSize: 12, color: 'var(--fg-mute)' }}>{leadResolved.role}</div>
            </div>
          </div>
        ) : client.lead_staff_id ? (
          <div className="muted-dash">Lead: {client.lead_staff_id}</div>
        ) : (
          <p className="client-create-help" style={{ margin: 0 }}>
            No lead assigned. Use <strong>Edit</strong> to assign one.
          </p>
        )}
      </section>

      {/* ── Authorised contacts ───────────────────────────────────────── */}
      <section className="card flush client-detail-section">
        <div className="client-detail-card-h">
          <h3 className="client-detail-h" style={{ margin: 0 }}>
            Authorised contacts ({contacts.length})
          </h3>
          {canInvite && (
            <button type="button" className="btn sm" onClick={() => setInviteOpen(true)}>
              + Invite
            </button>
          )}
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {contacts.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-mute)', fontSize: 13 }}>
                  No authorised contacts yet — invite one to grant portal access.
                </td>
              </tr>
            )}
            {contacts.map((c) => (
              <tr key={c.id}>
                <td>
                  <div style={{ fontWeight: 500 }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                    {c.id}
                  </div>
                </td>
                <td style={{ fontSize: 13, color: 'var(--fg-soft)' }}>{c.contact_role}</td>
                <td style={{ fontSize: 13 }}>{c.email}</td>
                <td style={{ fontSize: 13, color: 'var(--fg-soft)' }}>
                  {c.phone || <span className="muted-dash">—</span>}
                </td>
                <td>
                  <span className={`chip ${userStatusTone(c.status)}`}>
                    {USER_STATUS_LABEL[c.status]}
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  {/* Operator approval for self-signup contacts. Only shown
                      for invited rows + roles permitted to invite (same set
                      already gates the +Invite button above). */}
                  {c.status === 'invited' && canInvite && (
                    <button
                      type="button"
                      className="btn sm accent"
                      disabled={activatingId === c.id}
                      onClick={() => activateContact(c.id, c.name)}
                    >
                      {activatingId === c.id ? 'Activating…' : 'Activate'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ── Per-client drill-down — Requests ───────────────────────────── */}
      <section className="card flush client-detail-section">
        <div className="client-detail-card-h">
          <h3 className="client-detail-h" style={{ margin: 0 }}>Requests ({requests.length})</h3>
          <Link to="/admin/inbox" className="btn sm ghost">Full inbox →</Link>
        </div>
        {requests.length === 0 ? (
          <p className="client-create-help" style={{ padding: 24, margin: 0, textAlign: 'center' }}>
            No briefs yet for this client.
          </p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Headline</th>
                <th>Service</th>
                <th>Status</th>
                <th>Value</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => {
                const svc = SERVICES.find((s) => s.id === r.service);
                return (
                  <tr key={r.id}>
                    <td><Link to={`/admin/requests/${r.id}`} className="rid">{r.id}</Link></td>
                    <td style={{ fontSize: 13 }}>{r.headline}</td>
                    <td style={{ fontSize: 13, color: 'var(--fg-soft)' }}>{svc?.name ?? r.service}</td>
                    <td><StatusChip status={r.status} /></td>
                    <td style={{ fontSize: 13 }}>
                      {r.value_pence != null ? `£${(r.value_pence / 100).toLocaleString()}` : <span className="muted-dash">—</span>}
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--fg-mute)' }}>
                      {r.created_at.slice(0, 10)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Per-client drill-down — Proposals ──────────────────────────── */}
      <section className="card flush client-detail-section">
        <div className="client-detail-card-h">
          <h3 className="client-detail-h" style={{ margin: 0 }}>Proposals ({proposals.length})</h3>
          <Link to="/admin/proposals" className="btn sm ghost">All proposals →</Link>
        </div>
        {proposals.length === 0 ? (
          <p className="client-create-help" style={{ padding: 24, margin: 0, textAlign: 'center' }}>
            No proposals drafted for this client yet.
          </p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Brief</th>
                <th>Status</th>
                <th>Fee</th>
                <th>Plan</th>
                <th>Valid until</th>
              </tr>
            </thead>
            <tbody>
              {proposals.map((p) => (
                <tr key={p.id}>
                  <td><Link to={`/admin/proposals/${p.id}`} className="rid">{p.id}</Link></td>
                  <td style={{ fontSize: 13 }}>
                    <Link to={`/admin/requests/${p.request_id}`} className="rid">{p.request_id}</Link>
                  </td>
                  <td><span className={`chip ${proposalStatusTone(p.status)}`}>{p.status}</span></td>
                  <td style={{ fontSize: 13 }}>{fmtMoney(p.fee_pence, p.currency)}</td>
                  <td style={{ fontSize: 13, color: 'var(--fg-soft)' }}>{p.payment_plan}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--fg-mute)' }}>
                    {p.valid_until ? p.valid_until.slice(0, 10) : <span className="muted-dash">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Per-client drill-down — Invoices ───────────────────────────── */}
      <section className="card flush client-detail-section">
        <div className="client-detail-card-h">
          <h3 className="client-detail-h" style={{ margin: 0 }}>Invoices ({invoices.length})</h3>
          <Link to="/admin/finance" className="btn sm ghost">Finance dashboard →</Link>
        </div>
        {invoices.length === 0 ? (
          <p className="client-create-help" style={{ padding: 24, margin: 0, textAlign: 'center' }}>
            No invoices issued to this client yet.
          </p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Milestone</th>
                <th>Issued</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id}>
                  <td><Link to={`/admin/invoices/${i.id}`} className="rid">{i.id}</Link></td>
                  <td><span className={`chip ${invoiceStatusTone(i.status)}`}>{i.status}</span></td>
                  <td style={{ fontSize: 13 }}>{fmtMoney(i.amount_pence, i.currency)}</td>
                  <td style={{ fontSize: 13, color: 'var(--fg-soft)' }}>
                    {i.milestone_label ?? <span className="muted-dash">—</span>}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--fg-mute)' }}>
                    {i.issued_at ? i.issued_at.slice(0, 10) : <span className="muted-dash">—</span>}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--fg-mute)' }}>
                    {i.due_at ? i.due_at.slice(0, 10) : <span className="muted-dash">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Per-client drill-down — Documents ──────────────────────────── */}
      <section className="card flush client-detail-section">
        <div className="client-detail-card-h">
          <h3 className="client-detail-h" style={{ margin: 0 }}>Documents ({documents.length})</h3>
          <Link to="/admin/reports" className="btn sm ghost">Reports & deliverables →</Link>
        </div>
        {documents.length === 0 ? (
          <p className="client-create-help" style={{ padding: 24, margin: 0, textAlign: 'center' }}>
            No documents filed for this client yet.
          </p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Title</th>
                <th>Kind</th>
                <th>Status</th>
                <th>Visible</th>
                <th>Size</th>
                <th>Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((d) => (
                <tr key={d.id}>
                  <td>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{d.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                      {d.filename}
                    </div>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--fg-soft)' }}>{d.kind}</td>
                  <td><span className="chip">{d.status}</span></td>
                  <td>
                    <span className={`chip ${d.visible_to_client ? 'green' : ''}`}>
                      {d.visible_to_client ? 'client' : 'staff'}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--fg-mute)' }}>
                    {d.size_bytes < 1024 ? `${d.size_bytes} B`
                      : d.size_bytes < 1024 * 1024 ? `${(d.size_bytes / 1024).toFixed(1)} KB`
                      : `${(d.size_bytes / 1024 / 1024).toFixed(1)} MB`}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--fg-mute)' }}>
                    {d.created_at.slice(0, 10)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Audit timeline ─────────────────────────────────────────────── */}
      <section className="card flush client-detail-section">
        <div className="client-detail-card-h">
          <h3 className="client-detail-h" style={{ margin: 0 }}>
            Audit timeline ({audit.length})
          </h3>
          <Link
            to={`/admin/audit?resource=clients&resource_id=${client.id}`}
            className="btn sm ghost"
          >
            Full audit →
          </Link>
        </div>
        {audit.length === 0 ? (
          <p
            className="client-create-help"
            style={{ padding: 24, margin: 0, textAlign: 'center' }}
          >
            No audit entries yet for this client.
          </p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                <th>What</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--fg-mute)', whiteSpace: 'nowrap' }}>
                    {r.d} {r.t}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    <div>{r.actor_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--fg-mute)' }}>{r.actor_kind}</div>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--fg-soft)' }}>{r.what}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Modals / Drawers ──────────────────────────────────────────── */}
      {editOpen && (
        <ClientEditDrawer
          client={{
            id: client.id,
            name: client.name,
            trading_name: client.trading_name,
            registered_address: client.registered_address,
            website: client.website,
            lead_staff_id: client.lead_staff_id,
            status: client.status,
          }}
          onClose={() => setEditOpen(false)}
          onPatched={(next) => {
            // Merge the server's returned shape into our detail-page state.
            // Cast through unknown — server's redactClient widens the type
            // beyond our local ClientDto but the values match.
            setClient((prev) => (prev ? { ...prev, ...(next as unknown as Partial<ClientDto>) } : prev));
            // Drawer shows a "Changes saved" banner and closes itself after
            // ~1.2s via its own onClose callback. We just update state + flash.
            setFlash('Client updated.');
            // Re-pull audit so the new client.updated / client.status_changed
            // rows show up at the top of the timeline.
            refreshAudit();
          }}
        />
      )}
      {inviteOpen && (
        <InviteContactModal
          clientId={client.id}
          clientName={client.name}
          onClose={() => setInviteOpen(false)}
          onInvited={(invitedEmail) => {
            // Modal shows "Invite sent" and closes itself after ~1.2s via
            // its own onClose. Just refresh data + flash here.
            setFlash(`Invite sent to ${invitedEmail}.`);
            refreshContacts();
            refreshAudit();
          }}
        />
      )}
    </>
  );
}
